import { Component, OnInit, ViewChild, Input, Output, EventEmitter, OnDestroy, ElementRef, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CardComponent } from '../../card/card.component';
import { InputComponent } from '../../input/input.component';
import { ButtonComponent } from '../../button/button.component';
import { InputCheckboxComponent } from '../../input-checkbox/input-checkbox.component';
import { AlertCardComponent } from '../../alert-card/alert-card.component';
import { AuthService } from '../../../services/auth.service';
import { JobApplicationService, NonRegisteredUserApplication, RegisteredUserApplication, JobAttachment } from '../../../services/job-application.service';
import { ToastService } from '../../../services/toast.service';
import { ProfileFormComponent } from '../../profile-form/profile-form.component';
import { environment } from '../../../../environments/environment';
import { createNewUserConsents } from '../../../../../../../libs/shared/src/lib/models/consent.model';
import { combineLatest, Subject, takeUntil, debounceTime, forkJoin, of, Observable, from } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { getErrorTranslationKey } from '../../../../../../../libs/shared/src/lib/i18n/error-translations';
import { AttachmentService } from '../../../../../../../libs/shared/src/lib/services/attachment.service';
import { MatExpansionModule } from '@angular/material/expansion';

export type AttachmentType = 'cv' | 'coverLetter' | 'certificate' | 'portfolio' | 'other';

export interface FileAttachment {
  file: File;
  type: AttachmentType | null;
  uploading: boolean;
  uploaded: boolean;
  error: string | null;
  attachmentId?: string;
  attachmentUrl?: string;
}

// Interface for persisted form state (localStorage)
// Note: File attachments are NOT persisted - they are uploaded only on form submission
interface PersistedFormState {
  jobId: string;
  currentStep: number;
  email: string;
  gdprConsent: boolean;
  // Profile data for non-authorized users
  profileData?: any;
  savedAt: number; // timestamp
}

@Component({
  selector: 'app-job-apply-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    CardComponent,
    InputComponent,
    ButtonComponent,
    InputCheckboxComponent,
    AlertCardComponent,
    ProfileFormComponent,
    MatExpansionModule,
  ],
  templateUrl: './job-apply-form.component.html',
  styleUrl: './job-apply-form.component.scss',
})
export class JobApplyFormComponent implements OnInit, OnDestroy {
  @Input() jobId!: string; // Job ID for the application
  @Output() profileDataChange = new EventEmitter<void>(); // Emitted when user edits profile
  @ViewChild('attachmentFileInput') attachmentFileInput!: ElementRef<HTMLInputElement>;

  // Use setter to detect when profile form becomes available and subscribe to changes
  private _profileFormComponent: ProfileFormComponent | undefined;
  private profileFormSubscribed = false;

  @ViewChild('profileForm') set profileFormComponent(component: ProfileFormComponent | undefined) {
    this._profileFormComponent = component;
    // Subscribe to profile form changes when component becomes available
    if (component && !this.profileFormSubscribed) {
      this.profileFormSubscribed = true;

      if (!this.authService.isLoggedIn()) {
        // For non-logged-in users: Subscribe to personalInfoForm changes with debounce
        component.personalInfoForm?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(1000)).subscribe(() => {
          this.saveFormState();
          this.profileDataChange.emit(); // Notify parent that profile changed
        });

        // Subscribe to profile data changes (languages, skills, etc.)
        component.profileDataChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.saveFormState();
          this.profileDataChange.emit(); // Notify parent that profile changed
        });
      } else {
        // For logged-in users: Only subscribe to profile data changes to invalidate match score
        component.profileDataChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.profileDataChange.emit(); // Notify parent that profile changed (to clear match score)
        });
      }
    }
  }

  get profileFormComponent(): ProfileFormComponent | undefined {
    return this._profileFormComponent;
  }

  currentStep: number = 1;
  emailForm: FormGroup = this.fb.group({});
  resumeAndCoverForm: FormGroup = this.fb.group({});
  gdprConsentAcceptedAt: Date | null = null;

  gdprConsentHtml: string = '';

  // Loading and error states
  isSubmitting: boolean = false;
  submissionError: string | null = null;
  missingDataWarning: string | null = null;

  // Computed values to avoid function calls in template
  fullName: string = '';

  // Cleanup
  private readonly destroy$ = new Subject<void>();

  // Attachments
  attachments: FileAttachment[] = [];
  isDragOver = false;
  private readonly maxFileSize = 10 * 1024 * 1024; // 10MB
  private readonly allowedFileTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  // LocalStorage persistence
  private readonly STORAGE_KEY = 'jobApplyFormState';
  private readonly STATE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
  restoredProfileData: any = null; // Restored profile data for non-authorized users
  private readonly isBrowser: boolean;

  constructor(
    private readonly fb: FormBuilder,
    public authService: AuthService,
    private readonly translocoService: TranslocoService,
    private readonly jobApplicationService: JobApplicationService,
    private readonly toastService: ToastService,
    private readonly attachmentService: AttachmentService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.initForms();

    // Restore saved form state from localStorage
    this.restoreFormState();

    // Skip step 1 if user is already logged in (and no restored state)
    if (this.authService.isLoggedIn() && this.currentStep === 1) {
      // Use the logged-in user's email
      const userEmail = this.authService.currentUserValue?.email || '';
      this.emailForm.patchValue({ email: userEmail });
      // Skip to step 2
      this.currentStep = 2;
    }

    // Subscribe to form changes to save state
    this.emailForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.saveFormState());
    this.resumeAndCoverForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.saveFormState());

    // Update GDPR consent HTML when translations are loaded or language changes
    combineLatest([
      this.translocoService.selectTranslate('jobApply.gdprConsent.template'),
      this.translocoService.selectTranslate('jobApply.gdprConsent.termsLinkText'),
      this.translocoService.selectTranslate('jobApply.gdprConsent.privacyLinkText'),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([template, termsLinkText, privacyLinkText]) => {
        const termsUrl = environment.legal.termsOfUseUrl;
        const privacyUrl = environment.legal.privacyPolicyUrl;

        this.gdprConsentHtml = template
          .replace('{termsLink}', `<a href="${termsUrl}" target="_blank">${termsLinkText}</a>`)
          .replace('{privacyLink}', `<a href="${privacyUrl}" target="_blank">${privacyLinkText}</a>`);
      });
  }

  initForms(): void {
    // Step 1: Email form
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    // Step 3: Resume and Cover Letter form
    this.resumeAndCoverForm = this.fb.group({
      resume: [''],
      resumeFile: [null],
      resumeLanguage: ['', Validators.required],
      resumeUrl: [''],
      resumeType: [''],
      resumeFileType: [''],
      attachmentId: [''],
      gdprConsent: [false, Validators.requiredTrue],
    });
  }

  nextStep(): void {
    if (!this.emailForm.valid) return;
    if (this.currentStep < 3) {
      this.currentStep++;
      this.saveFormState();
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.saveFormState();
    }
  }

  submitApplication(): void {
    // Mark all form fields as touched to show validation errors
    this.markFormGroupTouched(this.resumeAndCoverForm);
    // Get user ID if logged in
    let userId = this.authService.currentUserValue?.id;

    // Get complete profile data from the profile form component
    const profileComponent = this.profileFormComponent;
    const profileData = profileComponent?.currentFormData || profileComponent?.fullUserProfile || profileComponent?.userProfile;

    // Check if all required forms are valid (not the unused personalInfoForm)
    // For job application, we need:
    // 1. Email (if not logged in)
    // 2. GDPR consent
    // 3. Profile data exists (personal info filled in ProfileFormComponent)

    const emailValid = this.authService.isLoggedIn() || this.emailForm.valid;
    const gdprConsentValid = this.resumeAndCoverForm.get('gdprConsent')?.valid;
    const profileDataExists = !!profileData && profileData.firstName && profileData.lastName && profileData.gender && profileData.telephone;

    const allFormsValid = emailValid && gdprConsentValid && profileDataExists;

    if (!allFormsValid) {
      // Show warning about missing data
      this.showMissingDataWarning(emailValid, gdprConsentValid, profileData);
      return;
    }

    // Clear any previous warnings
    this.missingDataWarning = null;

    if (allFormsValid) {
      // Save timestamp when GDPR consent was accepted
      this.gdprConsentAcceptedAt = new Date();

      // Clear previous errors and set loading state
      this.submissionError = null;
      this.isSubmitting = true;

      // First upload all pending attachments, then submit the application
      this.uploadAllAttachments()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (allUploadsSucceeded) => {
            if (!allUploadsSucceeded) {
              // Some uploads failed - show error and stop
              this.isSubmitting = false;
              this.toastService.error(
                this.translocoService.translate('jobApply.attachments.errors.uploadFailed'),
                this.translocoService.translate('jobApply.attachments.errors.uploadFailedDesc'),
                true,
                5000
              );
              return;
            }

            // All uploads succeeded - now submit the application
            this.submitApplicationData(profileComponent, profileData, userId);
          },
          error: () => {
            this.isSubmitting = false;
            this.toastService.error(
              this.translocoService.translate('jobApply.attachments.errors.uploadFailed'),
              this.translocoService.translate('jobApply.attachments.errors.uploadFailedDesc'),
              true,
              5000
            );
          },
        });
    }
  }

  /**
   * Submit the application data after attachments have been uploaded
   */
  private submitApplicationData(profileComponent: ProfileFormComponent | undefined, profileData: any, userId: number | undefined): void {
    // Prepare application data based on user login status
    let applicationData: RegisteredUserApplication | NonRegisteredUserApplication;

    if (this.authService.isLoggedIn()) {
      // LOGGED-IN USER: Send minimal data (profile already saved to backend)
      applicationData = {
        // Keep jobApplicationConsent for backward compatibility
        jobApplicationConsent: this.resumeAndCoverForm.get('gdprConsent')?.value,

        // Build unified consent structure from single checkbox
        consents: createNewUserConsents(
          this.resumeAndCoverForm.get('gdprConsent')?.value, // Privacy Policy
          this.resumeAndCoverForm.get('gdprConsent')?.value // Terms of Use (same checkbox)
          // Don't send marketing/aiMatching - backend already knows user's preferences
        ),

        // Resume data (if any)
        // resumeAttachment: {
        //   resumeFile: this.resumeAndCoverForm.get('resumeFile')?.value,
        //   resumeLanguage: this.resumeAndCoverForm.get('resumeLanguage')?.value,
        //   attachmentId: this.resumeAndCoverForm.get('attachmentId')?.value,
        // },

        // Additional attachments (CV, cover letter, other)
        jobAttachments: this.getJobAttachments(),

        // Metadata
        userLanguage: this.translocoService.getActiveLang(),
      };
    } else {
      // NON-LOGGED-IN USER: Send complete profile data
      const profileData = this.profileFormComponent?.currentFormData || this.profileFormComponent?.fullUserProfile || this.profileFormComponent?.userProfile;

      applicationData = {
        email: this.emailForm.get('email')?.value,

        // Complete Profile data from all sections
        profile: {
          // Personal Information
          title: profileData?.title || '',
          titleAfterName: profileData?.titleAfterName || '',
          firstName: profileData?.firstName || '',
          lastName: profileData?.lastName || '',
          gender: profileData?.gender || '',
          email: this.emailForm.get('email')?.value,
          telephoneNumber: profileData?.telephone || '',
          dateOfBirth: profileData?.birthDate || '',
          address: {
            formattedAddress: profileData?.address?.formattedAddress || '',
            streetName: profileData?.address?.streetName || '',
            streetNumber: profileData?.address?.streetNumber || '0',
            city: profileData?.address?.city || '',
            administrativeArea: profileData?.address?.administrativeArea || '',
            postalCode: profileData?.address?.postalCode || '0',
            country: profileData?.address?.country || '',
            latitude: profileData?.address?.latitude || 0,
            longitude: profileData?.address?.longitude || 0,
          },
          // additionalInfo: profileData?.additionalInfo || "",
          longcv: {
            // Professional Experience
            workExperiences: profileData?.workExperiences || [],
            projects: profileData?.projects || [],

            // Education
            universityEducations: profileData?.universityEducations || [],
            highSchoolEducations: profileData?.highSchoolEducations || [],

            // Skills & Languages
            skills: profileData?.skills || [],
            languages: profileData?.languages || [],
          },
        },

        // Resume data - get from ProfileFormComponent which has the actual data
        // resumeAttachment: {
        //   resumeFile: profileComponent?.resumeAndCoverForm.get('resumeFile')?.value || this.resumeAndCoverForm.get('resumeFile')?.value,
        //   url: profileComponent?.resumeAndCoverForm.get('resumeUrl')?.value || this.resumeAndCoverForm.get('resumeUrl')?.value,
        //   fileType: this.resumeAndCoverForm.get('resumeFileType')?.value,
        //   type: this.resumeAndCoverForm.get('resumeType')?.value,
        //   fileName: this.resumeAndCoverForm.get('resumeFileName')?.value,
        //   fileSize: this.resumeAndCoverForm.get('resumeFileSize')?.value,
        //   resumeLanguage: profileComponent?.resumeAndCoverForm.get('resumeLanguage')?.value || this.resumeAndCoverForm.get('resumeLanguage')?.value,
        //   attachmentId: profileComponent?.resumeAndCoverForm.get('attachmentId')?.value || this.resumeAndCoverForm.get('attachmentId')?.value,
        // },

        // Keep jobApplicationConsent for backward compatibility
        jobApplicationConsent: this.resumeAndCoverForm.get('gdprConsent')?.value,

        // Build unified consent structure from form values
        consents: createNewUserConsents(
          this.resumeAndCoverForm.get('gdprConsent')?.value, // Privacy Policy from checkbox
          this.resumeAndCoverForm.get('gdprConsent')?.value, // Terms of Use from same checkbox
          false, // Marketing (if provided in profile)
          profileData?.aiMatching, // AI Matching (from profile)
          profileData?.aiParsing // AI Parsing (from profile)
        ),

        // Additional attachments (CV, cover letter, other)
        jobAttachments: this.getJobAttachments(),

        // Metadata
        userLanguage: this.translocoService.getActiveLang(),
      };
    }

    // Submit the application using the appropriate endpoint
    const isRegisteredUser = this.authService.isLoggedIn();

    this.jobApplicationService.submitApplication(applicationData, isRegisteredUser, this.jobId, userId).subscribe({
      next: () => {
        this.isSubmitting = false;

        // Clear saved form state on successful submission
        this.clearFormState();

        // Update computed values for confirmation step
        this.updateComputedValues();

        // Success!
        // this.toastService.success(this.translocoService.translate('jobApply.success.applicationSubmitted'), 'Application submitted successfully', true, 3000);
        this.currentStep = 3; // Move to confirmation step
      },
      error: (error) => {
        this.isSubmitting = false;

        // Get error translation keys from shared error translations
        const titleKey = getErrorTranslationKey(error.error?.message, 'title');
        const messageKey = getErrorTranslationKey(error.error?.message, 'message');

        this.toastService.error(this.translocoService.translate(titleKey), this.translocoService.translate(messageKey), true, 5000);
      },
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get isSubmitDisabled(): boolean {
    // Check the same validation logic as submitApplication
    const profileComponent = this.profileFormComponent;
    const profileData = profileComponent?.currentFormData || profileComponent?.fullUserProfile || profileComponent?.userProfile;

    const emailValid = this.authService.isLoggedIn() || this.emailForm.valid;
    const gdprConsentValid = this.resumeAndCoverForm.get('gdprConsent')?.valid;
    const profileDataExists = !!profileData && profileData.firstName && profileData.lastName && profileData.gender && profileData.telephone;

    return !(emailValid && gdprConsentValid && profileDataExists);
  }

  /**
   * Check if required profile fields are filled in
   * Used to show/hide attachments section
   */
  get isProfileComplete(): boolean {
    const profileComponent = this.profileFormComponent;
    const profileData = profileComponent?.currentFormData || profileComponent?.fullUserProfile || profileComponent?.userProfile;

    return !!profileData && !!profileData.firstName && !!profileData.lastName && !!profileData.gender && !!profileData.telephone;
  }

  private updateComputedValues(): void {
    this.fullName = this.getFullName();
  }

  private getFullName(): string {
    const profileComponent = this.profileFormComponent;
    const profileData = profileComponent?.currentFormData || profileComponent?.fullUserProfile || profileComponent?.userProfile;

    if (profileData?.firstName && profileData?.lastName) {
      return `${profileData.firstName} ${profileData.lastName}`;
    }

    // Fallback for logged-in users
    if (this.authService.isLoggedIn() && this.authService.currentUserValue) {
      const user = this.authService.currentUserValue;
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
    }

    return 'N/A';
  }

  /**
   * Set warning message with list of missing required data
   */
  private showMissingDataWarning(emailValid: boolean, gdprConsentValid: boolean | undefined, profileData: any): void {
    const missingFields: string[] = [];

    if (!emailValid) {
      missingFields.push(this.translocoService.translate('jobApply.missingData.email'));
    }

    if (!profileData?.firstName) {
      missingFields.push(this.translocoService.translate('jobApply.missingData.firstName'));
    }

    if (!profileData?.lastName) {
      missingFields.push(this.translocoService.translate('jobApply.missingData.lastName'));
    }

    if (!profileData?.gender) {
      missingFields.push(this.translocoService.translate('jobApply.missingData.gender'));
    }

    if (!profileData?.telephone) {
      missingFields.push(this.translocoService.translate('jobApply.missingData.telephone'));
    }

    if (!gdprConsentValid) {
      missingFields.push(this.translocoService.translate('jobApply.missingData.gdprConsent'));
    }

    this.missingDataWarning = missingFields.join(', ');
  }

  // ==================== Attachment Methods ====================

  /**
   * Get uploaded attachments formatted for the API
   * Returns JobAttachment[] with only required fields: url, fileType, fileName
   */
  private getJobAttachments(): JobAttachment[] {
    return this.attachments
      .filter((a) => a.uploaded && a.type && a.attachmentUrl) // Only include successfully uploaded files with a type and url
      .map((a) => ({
        url: a.attachmentUrl || '',
        fileType: a.file.type,
        fileName: a.file.name,
        type: a.type || undefined,
        // model: 'Job application', // TODO: needs more changes in FE and BE to handle various models
      }));
  }

  onAttachmentFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.addFiles(Array.from(input.files));
      // Reset input so the same file can be selected again
      input.value = '';
    }
  }

  onBrowseClick(event: Event): void {
    // Prevent the click from bubbling up to the dropzone (which would trigger file input twice)
    event.stopPropagation();
    // Manually trigger the file input
    this.attachmentFileInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  private addFiles(files: File[]): void {
    for (const file of files) {
      // Validate file type
      if (!this.allowedFileTypes.includes(file.type)) {
        this.toastService.error(
          this.translocoService.translate('jobApply.attachments.errors.invalidType'),
          this.translocoService.translate('jobApply.attachments.errors.invalidTypeDesc'),
          true,
          5000
        );
        continue;
      }

      // Validate file size
      if (file.size > this.maxFileSize) {
        this.toastService.error(
          this.translocoService.translate('jobApply.attachments.errors.tooLarge'),
          this.translocoService.translate('jobApply.attachments.errors.tooLargeDesc'),
          true,
          5000
        );
        continue;
      }

      // Check for duplicate files
      const isDuplicate = this.attachments.some((a) => a.file.name === file.name && a.file.size === file.size);
      if (isDuplicate) {
        this.toastService.warning(
          this.translocoService.translate('jobApply.attachments.errors.duplicate'),
          this.translocoService.translate('jobApply.attachments.errors.duplicateDesc'),
          true,
          3000
        );
        continue;
      }

      const attachment: FileAttachment = {
        file,
        type: null,
        uploading: false,
        uploaded: false,
        error: null,
      };

      this.attachments.push(attachment);
    }
  }

  setAttachmentType(index: number, type: AttachmentType): void {
    if (index >= 0 && index < this.attachments.length) {
      this.attachments[index].type = type;
      // Files will be uploaded when form is submitted
    }
  }

  removeAttachment(index: number): void {
    if (index >= 0 && index < this.attachments.length) {
      this.attachments.splice(index, 1);
      // Note: No need to save state - attachments are not persisted to localStorage
    }
  }

  /**
   * Upload all pending attachments that have a type selected
   * Returns an Observable that completes when all uploads are done
   * @returns Observable<boolean> - true if all uploads succeeded, false if any failed
   */
  private uploadAllAttachments(): Observable<boolean> {
    // Get attachments that need to be uploaded (have type but not yet uploaded)
    const pendingAttachments = this.attachments.filter((a) => a.type && !a.uploaded && !a.uploading);

    if (pendingAttachments.length === 0) {
      // No attachments to upload, return success
      return of(true);
    }

    // Mark all pending as uploading
    pendingAttachments.forEach((a) => {
      a.uploading = true;
      a.error = null;
    });

    // Create upload observables for each attachment
    const uploadObservables = pendingAttachments.map((attachment) =>
      from(attachment.file.arrayBuffer()).pipe(
        switchMap((arrayBuffer: ArrayBuffer) => {
          const blob = new Blob([new Uint8Array(arrayBuffer)], { type: attachment.file.type });

          // Use appropriate upload method based on authentication status
          const uploadObservable = this.authService.isLoggedIn()
            ? this.attachmentService.sendCandidateAttachment(blob, attachment.file.type, attachment.file.name, attachment.type || undefined)
            : this.attachmentService.sendNotLogInCandidateAttachment(blob, attachment.file.type, attachment.type || undefined);

          return uploadObservable.pipe(
            map((response) => {
              if (response?.body) {
                attachment.uploading = false;
                attachment.uploaded = true;

                const attachmentData = Array.isArray(response.body) ? response.body[0] : response.body;

                if (this.authService.isLoggedIn()) {
                  // Authenticated users get attachment ID and URL
                  attachment.attachmentId = String(attachmentData.id);
                  attachment.attachmentUrl = attachmentData.url;
                } else {
                  // Non-authenticated users get S3 URL
                  attachment.attachmentUrl = typeof attachmentData === 'string' ? attachmentData : attachmentData.url;
                }
                return true;
              }
              return false;
            }),
            catchError((error) => {
              console.error('Error uploading attachment:', error);
              attachment.uploading = false;
              attachment.error = this.translocoService.translate('jobApply.attachments.errors.uploadFailed');
              return of(false);
            })
          );
        })
      )
    );

    // Execute all uploads in parallel and return true only if all succeeded
    return forkJoin(uploadObservables).pipe(
      map((results) => results.every((success) => success)),
      catchError(() => of(false))
    );
  }

  getFileIcon(mimeType: string): string {
    if (mimeType === 'application/pdf') {
      return 'fa-file-pdf';
    } else if (mimeType === 'application/msword' || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return 'fa-file-word';
    }
    return 'fa-file';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  // ==================== State Persistence Methods ====================

  /**
   * Save current form state to localStorage (client-side only)
   */
  private saveFormState(): void {
    if (!this.isBrowser || !this.jobId) return;

    const state: PersistedFormState = {
      jobId: this.jobId,
      currentStep: this.currentStep,
      email: this.emailForm.get('email')?.value || '',
      gdprConsent: this.resumeAndCoverForm.get('gdprConsent')?.value || false,
      savedAt: Date.now(),
    };

    // For non-authorized users, also save profile data
    if (!this.authService.isLoggedIn() && this.profileFormComponent) {
      state.profileData = this.profileFormComponent.currentFormData;
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save form state to localStorage:', e);
    }
  }

  /**
   * Restore form state from localStorage if available and valid (client-side only)
   */
  private restoreFormState(): void {
    if (!this.isBrowser || !this.jobId) return;

    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) return;

      const state: PersistedFormState = JSON.parse(savedState);

      // Check if state is for this job and not expired
      if (state.jobId !== this.jobId) {
        this.clearFormState();
        return;
      }

      if (Date.now() - state.savedAt > this.STATE_EXPIRY_MS) {
        this.clearFormState();
        return;
      }

      // Restore email (only for non-logged-in users)
      if (!this.authService.isLoggedIn() && state.email) {
        this.emailForm.patchValue({ email: state.email });
      }

      // Restore current step (but skip step 1 for logged-in users)
      if (this.authService.isLoggedIn()) {
        this.currentStep = Math.max(2, state.currentStep);
      } else {
        this.currentStep = state.currentStep;
      }

      // Restore GDPR consent
      if (state.gdprConsent) {
        this.resumeAndCoverForm.patchValue({ gdprConsent: state.gdprConsent });
      }

      // Note: Attachments are NOT restored - files cannot be persisted in localStorage
      // Users will need to re-select files after page reload

      // Restore profile data for non-authorized users
      if (!this.authService.isLoggedIn() && state.profileData) {
        this.restoredProfileData = state.profileData;
      }
    } catch (e) {
      console.warn('Failed to restore form state from localStorage:', e);
      this.clearFormState();
    }
  }

  /**
   * Clear saved form state from localStorage (client-side only)
   */
  clearFormState(): void {
    if (!this.isBrowser) return;

    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear form state from localStorage:', e);
    }
  }

  /**
   * Handle cancel action - clear state and navigate away
   */
  onCancel(): void {
    this.clearFormState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
